import { useEffect } from 'react';

const BACK_KEYS = new Set(['Escape', 'Backspace', 'BrowserBack']);
const BACK_CODES = new Set([461, 10009]);
const focusMemory = new Map<string, string>();

function center(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function focusables() {
  return Array.from(
    document.querySelectorAll<HTMLElement>('[data-focusable="true"]:not([disabled])'),
  ).filter(element => element.offsetParent !== null);
}

function moveFocus(direction: 'left' | 'right' | 'up' | 'down') {
  const candidates = focusables();
  if (!candidates.length) return;

  const current = document.activeElement instanceof HTMLElement && candidates.includes(document.activeElement)
    ? document.activeElement
    : candidates[0];
  const origin = center(current);
  const horizontal = direction === 'left' || direction === 'right';
  const sign = direction === 'left' || direction === 'up' ? -1 : 1;
  const currentZone = current.dataset.focusZone;

  const directional = candidates
    .filter(candidate => candidate !== current)
    .filter(candidate => {
      const point = center(candidate);
      const primary = horizontal ? point.x - origin.x : point.y - origin.y;
      return primary * sign > 8;
    });
  const sameZone = directional.filter(candidate => currentZone && candidate.dataset.focusZone === currentZone);
  const pool = sameZone.length ? sameZone : directional;
  const scored = pool.map(candidate => {
    const point = center(candidate);
    const primary = Math.abs(horizontal ? point.x - origin.x : point.y - origin.y);
    const secondary = Math.abs(horizontal ? point.y - origin.y : point.x - origin.x);
    return { candidate, score: primary + secondary * (sameZone.length ? 5 : 2.8) };
  })
    .sort((a, b) => a.score - b.score);

  const next = scored[0]?.candidate;
  if (!next) return;
  next.focus({ preventScroll: true });
  next.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
}

export function useRemoteNavigation(enabled: boolean, screenKey: string, onBack?: () => void) {
  useEffect(() => {
    if (!enabled) return;
    const remembered = focusMemory.get(screenKey);
    const initial = (remembered
      ? document.querySelector<HTMLElement>(`[data-focus-key="${CSS.escape(remembered)}"]`)
      : null) || document.querySelector<HTMLElement>('[data-autofocus="true"]')
      || document.querySelector<HTMLElement>('[data-focusable="true"]');
    const current = document.activeElement;
    const hasVisibleFocus = current instanceof HTMLElement
      && current.matches('[data-focusable="true"]:not([disabled])')
      && current.offsetParent !== null;
    if (!hasVisibleFocus) window.setTimeout(() => initial?.focus(), 0);

    const remember = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (target.dataset.focusKey) focusMemory.set(screenKey, target.dataset.focusKey);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (BACK_KEYS.has(event.key) || BACK_CODES.has(event.keyCode)) {
        event.preventDefault();
        onBack?.();
        return;
      }
      const directions: Record<string, 'left' | 'right' | 'up' | 'down'> = {
        ArrowLeft: 'left',
        ArrowRight: 'right',
        ArrowUp: 'up',
        ArrowDown: 'down',
      };
      const direction = directions[event.key];
      if (direction) {
        event.preventDefault();
        moveFocus(direction);
      }
    };
    document.addEventListener('focusin', remember);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('focusin', remember);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled, onBack, screenKey]);
}
